import { wipeData } from '@braze/web-sdk';
import { clearBrowserCookies, clearWebStorage } from '@/lib/storage/browserStorage';
import logger from './logger';

/**
 * Check if a key is related to Braze
 */
const isBrazeKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes('braze') ||
    lowerKey.includes('appboy') ||
    lowerKey.includes('ab.') ||
    lowerKey.includes('ab_')
  );
};

/**
 * Check if a Storage object has any Braze keys
 */
const storageHasBrazeKeys = (storage: Storage): boolean => {
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && isBrazeKey(key)) {
      return true;
    }
  }
  return false;
};

const isBrazeIndexedDBName = (name: string): boolean => {
  const lower = name.toLowerCase();
  return lower.includes('braze') || lower.includes('appboy');
};

type DeleteIndexedDBResult = 'success' | 'blocked';

const deleteIndexedDBDatabase = (dbName: string): Promise<DeleteIndexedDBResult> =>
  new Promise((resolve, reject) => {
    const deleteRequest = indexedDB.deleteDatabase(dbName);
    deleteRequest.onsuccess = () => resolve('success');
    deleteRequest.onerror = () =>
      reject(deleteRequest.error ?? new Error(`Failed to delete ${dbName}`));
    deleteRequest.onblocked = () => resolve('blocked');
  });

/**
 * Collect known + discovered Braze-related IndexedDB database names.
 */
const getBrazeIndexedDBNames = async (): Promise<string[]> => {
  const names = new Set<string>([
    'AppboyServiceWorkerAsyncStorage',
    'com.appboy.storage.tablestorage',
    'com.braze.storage.tablestorage',
    'Braze IndexedDB Support Test',
  ]);

  try {
    if (indexedDB.databases) {
      const databases = await indexedDB.databases();
      databases.forEach((db) => {
        if (db.name && isBrazeIndexedDBName(db.name)) {
          names.add(db.name);
        }
      });
    }
  } catch (error) {
    logger.warn('Error listing IndexedDB databases:', error);
  }

  return [...names];
};

/**
 * Check if cookies contain Braze-related data
 */
const isBrazeCookie = (name: string): boolean => {
  const lowerName = name.toLowerCase();
  return (
    lowerName.includes('braze') ||
    lowerName.includes('appboy') ||
    lowerName.includes('ab_') ||
    lowerName.includes('ab.')
  );
};

const cookiesHaveBrazeData = (): boolean => {
  const cookies = document.cookie.toLowerCase();
  return (
    cookies.includes('braze') ||
    cookies.includes('appboy') ||
    cookies.includes('ab_') ||
    cookies.includes('ab.')
  );
};

/**
 * Completely removes all Braze SDK data and persistence from the browser
 * This includes:
 * - IndexedDB storage
 * - Service worker registration
 * - Local storage keys
 * - Session storage keys
 * - Cookies
 */
export const cleanupBrazeCompletely = async (): Promise<void> => {
  try {
    // 1. Use Braze's built-in wipeData method to clear SDK data
    try {
      wipeData();
    } catch (error) {
      logger.warn('Error wiping Braze data:', error);
    }

    // 2. Delete IndexedDB databases (known names + any discovered braze/appboy DBs)
    const indexedDBNames = await getBrazeIndexedDBNames();

    for (const dbName of indexedDBNames) {
      try {
        const result = await deleteIndexedDBDatabase(dbName);
        if (result === 'blocked') {
          logger.warn(`IndexedDB deletion blocked for: ${dbName}`);
        }
      } catch (error) {
        logger.warn(`Error deleting IndexedDB ${dbName}:`, error);
      }
    }

    // 3. Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          const scriptURL = registration.active?.scriptURL || '';

          // Skip if scriptURL is empty to avoid URL construction errors
          if (scriptURL) {
            const url = new URL(scriptURL);
            const isBrazeWorker =
              url.pathname === '/braze-service-worker.js' ||
              url.pathname === '/service-worker.js' || // Legacy name (for cleanup of old installs)
              scriptURL.toLowerCase().includes('braze') ||
              scriptURL.toLowerCase().includes('appboy');

            if (isBrazeWorker) {
              await registration.unregister();
            }
          }
        }
      } catch (error) {
        logger.warn('Error unregistering service workers:', error);
      }
    }

    // 4. Clear localStorage/sessionStorage keys using shared consent cleanup helpers.
    clearWebStorage(() => window.localStorage, isBrazeKey);
    clearWebStorage(() => window.sessionStorage, isBrazeKey);

    // 5. Clear Braze cookies (JS-accessible only) using shared cross-domain deletion helper.
    clearBrowserCookies(isBrazeCookie);
  } catch (error) {
    logger.error('Error during Braze cleanup:', error);
  }
};

/**
 * Check if Braze has any persisted data in the browser
 */
export const hasBrazePersistence = (): boolean => {
  try {
    return (
      storageHasBrazeKeys(localStorage) ||
      storageHasBrazeKeys(sessionStorage) ||
      cookiesHaveBrazeData()
    );
  } catch (error) {
    logger.warn('Error checking Braze persistence:', error);
    return false;
  }
};
